<template>
    <div class="my-8 min-h-[60px] w-full">
        <button
            type="button"
            class="block w-full cursor-pointer text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-700"
            :tabindex="fitBarVisible && fitBarRating !== null ? 0 : -1"
            :aria-hidden="!(fitBarVisible && fitBarRating !== null)"
            @click="openFasletWidget">
            <span class="block text-xs font-semibold tracking-wide text-black uppercase mb-1">
                {{ fitBarTitle }}
            </span>
            <span class="sr-only">{{ fitBarOpenAction }}</span>
            <div class="relative mx-0 min-h-[10px] w-full" aria-hidden="true">
                <!-- Pointer: flat top, tapers to tip — sits on the scale line -->
                <div
                    class="pointer-events-none absolute -bottom-[8px] z-[1] flex -translate-x-1/2 flex-col items-center transition-[left] duration-300 ease-out"
                    :style="{ left: `${fitBarMeterPercent}%` }">
                    <div class="h-2 w-2 shrink-0 rounded-[1px] bg-green-700" />
                    <div
                        class="h-0 w-0 shrink-0 border-t-[6px] border-r-[4px] border-l-[4px] border-t-green-700 border-r-transparent border-l-transparent" />
                </div>
                <div class="absolute right-0 bottom-0 left-0 h-[3px] rounded-full bg-green-400" />
            </div>
            <div class="mt-3 flex items-center justify-between" aria-hidden="true">
                <span class="text-xs text-black">{{ fitBarScaleLow }}</span>
                <span class="text-xs text-black">{{ fitBarScaleHigh }}</span>
            </div>
        </button>
    </div>
</template>

<script setup lang="ts">
    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;

    const fitBarVisible = ref(false);
    const fitBarRating = ref<number | null>(null);

    const fitBarTitle = computed(() => snippets['headless.pdp.fasletFitBarTitle'] ?? 'Pasvorm');
    const fitBarScaleLow = computed(() => snippets['headless.pdp.fasletFitBarScaleLow'] ?? 'Valt klein');
    const fitBarScaleHigh = computed(() => snippets['headless.pdp.fasletFitBarScaleHigh'] ?? 'Valt groot');

    const fitBarOpenAction = computed(() => snippets['headless.pdp.fasletFitBarOpenAction'] ?? 'Opent maatadvies.');

    /**
     * Maps Faslet fit score to bar position.
     * Scale: -1 = small, -0.5 = smaller, 0 = perfect fit (centered), 0.5 = bigger, 1 = big.
     * Clamped to 5%–95% so the pointer stays visually inside the bar.
     */
    const fitBarMeterPercent = computed(() => {
        const v = fitBarRating.value;
        if (v === null || Number.isNaN(v)) {
            return 50;
        }
        return Math.min(95, Math.max(5, 50 + v * 45));
    });

    let fitRatingHandler: ((fitRating: number, config: { visible: boolean }) => void) | null = null;

    function attachFitRatingCallback() {
        const w = window as Window & {
            _faslet?: {
                onFitRating?: (fitRating: number, config: { visible: boolean }) => void;
            };
        };
        if (!w._faslet) {
            return;
        }
        fitRatingHandler = (fitRating: number, config: { visible: boolean }) => {
            fitBarRating.value = fitRating;
            fitBarVisible.value = config.visible;
        };
        w._faslet.onFitRating = fitRatingHandler;
    }

    function openFasletWidget() {
        const w = window as Window & { _faslet?: { openWidget?: () => void } };
        w._faslet?.openWidget?.();
    }

    function detachFitRatingCallback() {
        const w = window as Window & {
            _faslet?: { onFitRating?: (fitRating: number, config: { visible: boolean }) => void };
        };
        if (w._faslet?.onFitRating === fitRatingHandler) {
            delete w._faslet.onFitRating;
        }
        fitRatingHandler = null;
    }

    defineExpose({
        attachFitRatingCallback,
    });

    onBeforeUnmount(() => {
        detachFitRatingCallback();
        fitBarVisible.value = false;
        fitBarRating.value = null;
    });
</script>
